import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PageSEO } from "@/components/SEO/PageSEO";
import "../styles/legal.css";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageSEO
        title="Privacy Policy"
        description="AI Genius Automations Privacy Policy. Learn what information we collect, how we use it, and the choices you have."
        keywords="privacy policy, data protection, AI Genius Automations"
        noindex={false}
      />

      <div className="legal-page">
        <div className="legal-container">
          <button
            onClick={() => navigate(-1)}
            className="legal-back-button"
            aria-label="Go back"
          >
            ← Back
          </button>

          <h1 className="legal-title">Privacy Policy</h1>

          <div className="legal-meta">
            <span>Last Updated: May 20, 2026</span>
          </div>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              AI Genius Automations ("AIG," "we," "us," or "our") operates the
              website at aigeniusautomations.com, the courses portal at
              courses.aigeniusautomations.com, and related tools and services
              (together, the "Services"). This Privacy Policy explains what
              information we collect, how we use it, and the choices you have.
            </p>
            <p>
              By using the Services you agree to this Privacy Policy. If you do
              not agree, please do not use the Services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <ul>
              <li>
                <strong>Account information:</strong> name and email address
                used to create an account.
              </li>
              <li>
                <strong>Contact form submissions:</strong> the name, email, and
                message you submit through our contact form.
              </li>
              <li>
                <strong>Billing information:</strong> if you purchase a
                subscription or course, payment is processed by Stripe. We do
                not store full card numbers; we store a Stripe customer
                identifier and the resulting subscription or purchase status.
              </li>
              <li>
                <strong>Course activity:</strong> course enrollments, lesson
                progress, bookmarks, and other actions you take inside the
                courses portal.
              </li>
              <li>
                <strong>Optional profile data:</strong> profile picture (stored
                in your browser's local storage) and any preferences you choose
                to set.
              </li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li>
                <strong>Device and connection data:</strong> IP address, browser
                type, and operating system, used for security and debugging.
              </li>
              <li>
                <strong>Usage data:</strong> pages visited and features used,
                used to operate and improve the Services.
              </li>
              <li>
                <strong>Cookies:</strong> a session cookie set after you sign
                in, plus cookies required for basic site functionality. See
                Section 7.
              </li>
            </ul>

            <p>
              We do not currently train AI models on your personal data. The
              Services may use third-party AI APIs to generate responses; in
              those cases we send only the inputs needed to fulfill your
              request.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <ul>
              <li>Provide, operate, and maintain the Services.</li>
              <li>
                Authenticate you, manage your account, and keep your session
                secure.
              </li>
              <li>
                Process payments and manage subscriptions and course purchases
                through Stripe.
              </li>
              <li>
                Deliver course content, track your progress, and enable features
                like enrollment and bookmarks.
              </li>
              <li>
                Respond to support requests and other messages you send us.
              </li>
              <li>
                Send transactional emails (account, billing, and course-related
                notifications) and, with your consent, marketing emails.
              </li>
              <li>
                Detect and prevent fraud, abuse, and security incidents, and
                comply with legal obligations.
              </li>
              <li>
                Understand how the Services are used so we can improve them.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Share Your Information</h2>

            <h3>4.1 We Do Not Sell Your Data</h3>
            <p>We do not sell, rent, or trade your personal information.</p>

            <h3>4.2 Service Providers</h3>
            <p>
              We share information with service providers who help us operate
              the Services, under contractual obligations to protect it and use
              it only on our behalf. These currently include:
            </p>
            <ul>
              <li>
                <strong>Supabase</strong> — authentication, database, and
                hosting.
              </li>
              <li>
                <strong>Stripe</strong> — payment processing for subscriptions
                and one-time course purchases.
              </li>
              <li>
                <strong>Email delivery providers</strong> — for transactional
                and (where applicable) marketing email.
              </li>
            </ul>
            <p>
              This list may change as our infrastructure evolves; we will update
              this policy when it does.
            </p>

            <h3>4.3 Legal Requirements</h3>
            <p>
              We may disclose information when we believe in good faith it is
              necessary to:
            </p>
            <ul>
              <li>Comply with a law, regulation, subpoena, or court order;</li>
              <li>
                Protect the rights, property, or safety of AIG, our users, or
                others; or
              </li>
              <li>
                Investigate or prevent fraud, abuse, or security incidents.
              </li>
            </ul>

            <h3>4.4 Business Transfers</h3>
            <p>
              If AIG is involved in a merger, acquisition, financing, or sale of
              assets, your information may be transferred as part of that
              transaction. We will notify you of any such change of control.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Retention</h2>
            <p>
              We retain personal information for as long as your account is
              active and for a reasonable period afterward to comply with our
              legal obligations, resolve disputes, and enforce our agreements.
              Billing and transaction records may be retained for longer where
              required by tax or financial regulations.
            </p>
            <p>
              You can request deletion of your account and associated personal
              information at any time (see Section 6).
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Your Choices and Rights</h2>
            <p>
              Depending on where you live, you may have rights to access,
              correct, delete, or restrict the use of your personal information,
              to receive a portable copy of it, and to object to certain
              processing. California residents have rights under the California
              Consumer Privacy Act (CCPA), and residents of the European
              Economic Area, United Kingdom, and Switzerland have rights under
              the GDPR and equivalent laws.
            </p>
            <p>
              To exercise these rights, reach out through our{" "}
              <Link to="/contact">contact form</Link>. You can also unsubscribe
              from marketing emails using the link in any marketing message.
            </p>
            <p>
              We will not discriminate against you for exercising your privacy
              rights.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Cookies</h2>
            <p>
              We use cookies that are strictly necessary for the Services to
              function — for example, a session cookie that keeps you signed in.
              We may also use a small number of cookies for analytics or
              functionality preferences. You can control cookies through your
              browser settings; disabling required cookies will prevent you from
              signing in.
            </p>
            <p>
              Our Services currently do not respond to "Do Not Track" browser
              signals.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Security</h2>
            <p>
              We rely on our infrastructure providers (including Supabase and
              Stripe) for industry-standard security practices, including
              encryption of data in transit over TLS. No method of transmission
              or storage is completely secure, and we cannot guarantee absolute
              security. You are responsible for keeping your account credentials
              confidential and for promptly notifying us of any suspected
              unauthorized access.
            </p>
            <p>
              If we become aware of a data breach affecting your personal
              information, we will notify you and any regulators as required by
              applicable law.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
              We operate in the United States. If you access the Services from
              outside the United States, your information will be transferred
              to, stored, and processed in the United States or in other
              countries where our service providers operate. Where required, we
              rely on appropriate legal mechanisms (such as Standard Contractual
              Clauses) for those transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              The Services are not directed to children under 16, and we do not
              knowingly collect personal information from children under 16. If
              you believe a child has provided us with personal information,
              please contact us and we will delete it.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make
              material changes, we will update the "Last Updated" date above
              and, where appropriate, notify you by email or through the
              Services. Your continued use of the Services after the changes
              take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact</h2>
            <p>
              Questions about this Privacy Policy or our handling of your
              information? Send us a message through our{" "}
              <Link to="/contact">contact form</Link> and we'll get back to you.
            </p>
            <div className="contact-info">
              <p>
                <strong>AI Genius Automations</strong>
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
